
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { TrendingUp, TrendingDown, BarChart3, Activity } from 'lucide-react';

interface CorrelationData {
  metric1: string;
  metric2: string;
  correlation: number;
  strength: 'weak' | 'moderate' | 'strong';
  direction: 'positive' | 'negative';
  insight: string;
}

export const CorrelationAnalysis: React.FC = () => {
  const { user } = useAuth();

  const { data: correlations, isLoading } = useQuery({
    queryKey: ['correlation-analysis'],
    queryFn: async () => {
      // Get body composition data
      let { data: bodyData, error: bodyError } = await supabase
        .from('body_composition')
        .select('total_weight, body_fat_percentage, skeletal_muscle_mass, recorded_date')
        .order('recorded_date', { ascending: true });

      // Get running data
      let { data: runData, error: runError } = await supabase
        .from('running_performance')
        .select('time_seconds, heart_rate_avg, distance_type, recorded_date')
        .eq('distance_type', '5k')
        .order('recorded_date', { ascending: true });

      // Check if we have sufficient data for analysis
      if ((!bodyData || bodyData.length < 5) && (!runData || runData.length < 5)) {
        return [];
      }

      if (bodyError || runError) throw bodyError || runError;

      const correlations: CorrelationData[] = [];

      // Calculate correlations between body composition metrics
      if (bodyData.length >= 5) {
        const weightBodyFatCorr = calculateCorrelation(
          bodyData.map(d => d.total_weight || 0),
          bodyData.map(d => d.body_fat_percentage || 0)
        );

        const weightMuscleCorr = calculateCorrelation(
          bodyData.map(d => d.total_weight || 0),
          bodyData.map(d => d.skeletal_muscle_mass || 0)
        );

        const bodyFatMuscleCorr = calculateCorrelation(
          bodyData.map(d => d.body_fat_percentage || 0),
          bodyData.map(d => d.skeletal_muscle_mass || 0)
        );

        correlations.push(
          {
            metric1: 'Weight',
            metric2: 'Body Fat %',
            correlation: weightBodyFatCorr,
            strength: getCorrelationStrength(weightBodyFatCorr),
            direction: weightBodyFatCorr > 0 ? 'positive' : 'negative',
            insight: getWeightBodyFatInsight(weightBodyFatCorr)
          },
          {
            metric1: 'Weight',
            metric2: 'Skeletal Muscle Mass',
            correlation: weightMuscleCorr,
            strength: getCorrelationStrength(weightMuscleCorr),
            direction: weightMuscleCorr > 0 ? 'positive' : 'negative',
            insight: getWeightMuscleInsight(weightMuscleCorr)
          },
          {
            metric1: 'Body Fat %',
            metric2: 'Skeletal Muscle Mass',
            correlation: bodyFatMuscleCorr,
            strength: getCorrelationStrength(bodyFatMuscleCorr),
            direction: bodyFatMuscleCorr > 0 ? 'positive' : 'negative',
            insight: getBodyFatMuscleInsight(bodyFatMuscleCorr)
          }
        );
      }

      // Calculate correlation between body composition and running performance
      if (bodyData.length >= 3 && runData.length >= 3) {
        // Match dates within reasonable ranges
        const matchedData = bodyData
          .map(body => {
            const bodyDate = new Date(body.recorded_date);
            const matchedRun = runData.find(run => {
              const runDate = new Date(run.recorded_date);
              const diffDays = Math.abs((bodyDate.getTime() - runDate.getTime()) / (1000 * 60 * 60 * 24));
              return diffDays <= 30; // Within 30 days
            });
            return matchedRun ? { ...body, runTime: matchedRun.time_seconds } : null;
          })
          .filter(Boolean);

        if (matchedData.length >= 3) {
          const bodyFatRunCorr = calculateCorrelation(
            matchedData.map(d => d!.body_fat_percentage || 0),
            matchedData.map(d => d!.runTime || 0)
          );

          correlations.push({
            metric1: 'Body Fat %',
            metric2: '5K Time',
            correlation: bodyFatRunCorr,
            strength: getCorrelationStrength(bodyFatRunCorr),
            direction: bodyFatRunCorr > 0 ? 'positive' : 'negative',
            insight: getBodyFatRunTimeInsight(bodyFatRunCorr)
          });
        }
      }

      return correlations;
    },
  });

  const calculateCorrelation = (x: number[], y: number[]): number => {
    const n = Math.min(x.length, y.length);
    if (n < 2) return 0;

    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.map((xi, i) => xi * y[i]).reduce((a, b) => a + b, 0);
    const sumX2 = x.map(xi => xi * xi).reduce((a, b) => a + b, 0);
    const sumY2 = y.map(yi => yi * yi).reduce((a, b) => a + b, 0);

    const numerator = n * sumXY - sumX * sumY;
    const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));

    return denominator === 0 ? 0 : numerator / denominator;
  };

  const getCorrelationStrength = (corr: number): 'weak' | 'moderate' | 'strong' => {
    const abs = Math.abs(corr);
    if (abs >= 0.7) return 'strong';
    if (abs >= 0.3) return 'moderate';
    return 'weak';
  };

  const getWeightBodyFatInsight = (corr: number): string => {
    if (Math.abs(corr) < 0.3) return "Weight and body fat show little correlation - focus on body composition over scale weight";
    if (corr > 0.5) return "Weight increases correlate with body fat increases - monitor both metrics closely";
    return "Moderate relationship between weight and body fat changes";
  };

  const getWeightMuscleInsight = (corr: number): string => {
    if (corr > 0.5) return "Weight gains correlate with muscle gains - good progress building lean mass";
    if (corr < -0.3) return "Weight decreases while maintaining muscle - excellent body recomposition";
    return "Weight and muscle mass show moderate relationship";
  };

  const getBodyFatMuscleInsight = (corr: number): string => {
    if (corr < -0.5) return "As muscle increases, body fat decreases - excellent body recomposition progress";
    if (corr > 0.3) return "Monitor training intensity - both muscle and fat may be changing together";
    return "Body fat and muscle mass show independent changes";
  };

  const getBodyFatRunTimeInsight = (corr: number): string => {
    if (corr > 0.5) return "Higher body fat correlates with slower run times - body composition affects performance";
    if (corr < -0.3) return "Interesting negative correlation - investigate training factors";
    return "Body fat and running performance show some relationship";
  };

  const getStrengthColor = (strength: string) => {
    switch (strength) {
      case 'strong': return 'bg-red-100 text-red-800';
      case 'moderate': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Correlation Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">Analyzing correlations...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-500" />
          Correlation Analysis
        </CardTitle>
        <CardDescription>Discover relationships between your fitness metrics</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {correlations && correlations.length > 0 ? (
            correlations.map((corr, index) => (
              <div key={index} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1">
                      {corr.direction === 'positive' ? (
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-600" />
                      )}
                      <span className="font-medium">{corr.metric1} ↔ {corr.metric2}</span>
                    </div>
                    <Badge className={getStrengthColor(corr.strength)}>
                      {corr.strength}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold">
                      {(corr.correlation * 100).toFixed(0)}%
                    </div>
                    <div className="text-xs text-gray-500">correlation</div>
                  </div>
                </div>
                <p className="text-sm text-gray-600">{corr.insight}</p>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>Add more data entries to see correlation analysis</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
